// ===========================================
// "Reminders on hold" for one invoice (admin) — #372 plan A6.3
// ===========================================
// The per-invoice stop: while held, no reminder step and no late fee is sent or charged, and
// "Send reminder now" is refused. The note is staff-only. Changing it needs
// `operations_invoices.write` AND `operations_pricing.delete`; without them the state is shown,
// read-only.

import { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import type { Invoice } from '../../api/invoices.api';
import { formatInSchoolTz } from '../../utils/dateUtils';
import { useHoldReminders, useReleaseReminders } from '../../hooks/useInvoices';
import LateFeeReasonDialog from './LateFeeReasonDialog';

const DAY: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

export interface RemindersHoldControlProps {
  invoice: Pick<Invoice, 'id' | 'status' | 'remindersOnHoldAt' | 'remindersOnHoldByName' | 'remindersOnHoldNote'>;
  timeZone: string;
  canChange: boolean;
}

/** Statuses that can still owe money, so a hold means something (the server's rule). */
const HOLDABLE = new Set(['SENT', 'OVERDUE', 'PARTIALLY_PAID']);

export default function RemindersHoldControl({ invoice, timeZone, canChange }: RemindersHoldControlProps) {
  const [open, setOpen] = useState(false);
  const hold = useHoldReminders();
  const release = useReleaseReminders();

  if (invoice.remindersOnHoldAt) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          canChange ? (
            <Button color="inherit" size="small" onClick={() => release.mutate(invoice.id)} disabled={release.isPending}>
              Release
            </Button>
          ) : undefined
        }
      >
        Reminders and late fees are on hold since {formatInSchoolTz(invoice.remindersOnHoldAt, timeZone, DAY)}
        {invoice.remindersOnHoldByName ? ` (${invoice.remindersOnHoldByName})` : ''}
        {invoice.remindersOnHoldNote ? `: ${invoice.remindersOnHoldNote}` : ''}
      </Alert>
    );
  }

  if (!canChange || !HOLDABLE.has(invoice.status)) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          hold.reset();
          setOpen(true);
        }}
      >
        Put reminders on hold
      </Button>
      <LateFeeReasonDialog
        open={open}
        title="Put reminders on hold"
        explanation={[
          'No reminder emails and no late fees go out for this invoice while it is on hold, and "Send reminder now" is turned off.',
          'Release the hold when you are ready: they start again from the next run. Nothing missed while on hold is sent later.',
        ]}
        label="Why? (for example: payment plan agreed)"
        confirmLabel="Put on hold"
        loading={hold.isPending}
        error={hold.error}
        onClose={() => setOpen(false)}
        onConfirm={(note) => hold.mutate({ id: invoice.id, note }, { onSuccess: () => setOpen(false) })}
      />
    </Box>
  );
}
